import {
    ArrowBack as ArrowBackIcon,
    Delete as DeleteIcon,
    Group as GroupIcon,
    Save as SaveIcon
} from '@mui/icons-material';
import {
    Box,
    Button,
    CardContent,
    Checkbox,
    Chip,
    FormControlLabel,
    FormGroup,
    Grid,
    Stack,
    Typography,
    Switch,
} from '@mui/material';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { useNavigate, useParams } from 'react-router-dom';

import { confirmToast, errorToast, successToast } from '@/common/toast';
import { Container, ErrorMessage, Input, InfoLabel, SectionHeader, StyledCard, LoadingState, ErrorState, EmptyState } from '@/components/common';
import { useDeleteGroup, useGroupDetail, useUpdateGroup } from '@/hooks/groups.hook';
import { groups } from '@/constants';
import { formatDate } from '@/util/dateUtils';
import { getGroupCourseRequirementIssues } from '@/util/groupCourseRequirements';
import CourseSelectPanel from './components/CourseSelectPanel';
import GroupRequirementAlerts, { CompletionRequirementOptionLabel } from './components/GroupRequirementAlerts';

/**
 * 그룹 상세 페이지 컴포넌트
 * 
 * 그룹의 상세 정보를 조회하고 수정할 수 있는 페이지입니다.
 */
const GroupDetailPage = () => {
    const { groupId } = useParams();
    const navigate = useNavigate();

    // 그룹 상세 정보 조회
    const { data: detailData, isLoading, error } = useGroupDetail(groupId);

    // 폼 관리
    const {
        control,
        handleSubmit,
        reset,
        watch,
        formState: { errors, isDirty },
    } = useForm({
        defaultValues: {
            groupId: '',
            groupName: '',
            groupCourse: '',
            groupCourseCount: '',
            groupStartDate: '',
            groupDueDate: '',
            groupAssessId: '',
            courseIdList: [],
            completionRequirements: [],
            aiCodeReviewEnabled: false,
        },
    });

    // 3. [개선된 로직] 데이터가 로드되면 폼 값 세팅 (useEffect 하나로 통합)
    useEffect(() => {
        // 데이터가 아직 안 왔으면 리턴
        if (!detailData || !detailData.groupInfo) return;

        const { groupInfo, courseSummary } = detailData;

        // 과정 ID 목록 추출 (여기서 바로 계산)
        const currentCourseIds = courseSummary
            ? courseSummary.map(c => c.courseId).filter(id => id != null)
            : [];

        // 폼 리셋 (서버 데이터 -> 폼 데이터 매핑)
        reset({
            groupId: groupInfo.groupId || '',
            groupName: groupInfo.groupName || '',
            groupCourse: groupInfo.groupCourse || '',
            groupCourseCount: groupInfo.groupCourseCount || '',
            // 날짜 포맷 변환 (null이면 빈 문자열 처리 필수)
            groupStartDate: formatDate(groupInfo.groupStartDate) || '',
            groupDueDate: formatDate(groupInfo.groupDueDate) || '',
            groupAssessId: groupInfo.groupAssessId || '',
            courseIdList: currentCourseIds,
            completionRequirements: groups.sanitizeCompletionRequirements(groupInfo.completionRequirements),
            aiCodeReviewEnabled: groupInfo.aiCodeReviewEnabled || false,
        });

    }, [detailData, reset]);

    // API 응답 구조: { groupInfo, courseSummary }
    const groupInfo = detailData?.groupInfo;
    const courseSummary = detailData?.courseSummary || [];

    // 수정, 삭제 훅
    const updateGroupMutation = useUpdateGroup();
    const deleteGroupMutation = useDeleteGroup();

    // 수료 조건 감시 (선택된 항목 표시용)
    const selectedCompletionRequirements = watch('completionRequirements');
    const selectedCourseIds = watch('courseIdList');
    const [selectedCourses, setSelectedCourses] = useState([]);
    const selectableCompletionRequirements = groups.getSelectableCompletionRequirements();
    const handleSelectedCoursesChange = useCallback((courses) => {
        setSelectedCourses((prev) => {
            if (
                prev.length === courses.length
                && prev.every((course, index) => (
                    course.courseId === courses[index]?.courseId
                    && Boolean(course.hasTraining) === Boolean(courses[index]?.hasTraining)
                ))
            ) {
                return prev;
            }
            return courses;
        });
    }, []);

    const requirementIssues = useMemo(
        () => getGroupCourseRequirementIssues({
            requirements: selectedCompletionRequirements,
            courseIds: selectedCourseIds,
            selectedCourses,
        }),
        [selectedCompletionRequirements, selectedCourseIds, selectedCourses]
    );

    /**
     * 그룹 정보 수정 핸들러
     */
    const onSubmit = useCallback(
        (data) => {
            const courseIdList = Array.isArray(data.courseIdList) ? data.courseIdList : [];
            const completionRequirements = groups.sanitizeCompletionRequirements(data.completionRequirements);
            const issues = getGroupCourseRequirementIssues({
                requirements: completionRequirements,
                courseIds: courseIdList,
                selectedCourses,
            });

            if (issues.errors.length > 0) {
                errorToast(issues.errors[0]);
                return;
            }

            const saveGroup = () => {
                updateGroupMutation.mutate(
                    {
                        groupId: Number(groupId),
                        groupName: data.groupName,
                        groupCourse: data.groupCourse,
                        groupCourseCount: data.groupCourseCount ? Number(data.groupCourseCount) : 0,
                        groupStartDate: data.groupStartDate,
                        groupDueDate: data.groupDueDate,
                        groupAssessId: data.groupAssessId ? Number(data.groupAssessId) : null,
                        courseIdList,
                        completionRequirements,
                        aiCodeReviewEnabled: data.aiCodeReviewEnabled || false,
                        groupStatus: groupInfo?.groupStatus ?? null,
                        groupCompletePercentage: groupInfo?.groupCompletePercentage ?? null,
                    },
                    {
                        onSuccess: () => {
                            successToast('그룹 정보가 수정되었습니다.');
                        },
                        onError: (error) => {
                            errorToast(`그룹 정보 수정에 실패했습니다. - ${error.response?.data?.msg}`);
                        },
                    }
                );
            };

            if (issues.warnings.length > 0) {
                confirmToast(`${issues.warnings[0]} 그래도 저장하시겠습니까?`, saveGroup);
                return;
            }

            saveGroup();
        },
        [groupId, updateGroupMutation, groupInfo, selectedCourses]
    );

    /**
     * 그룹 삭제 핸들러
     */
    const handleDelete = useCallback(() => {
        confirmToast('정말로 이 그룹을 삭제하시겠습니까?', () => {
            deleteGroupMutation.mutate(groupId, {
                onSuccess: () => {
                    successToast('그룹이 삭제되었습니다.');
                    navigate('/groups');
                },
                onError: (error) => {
                    errorToast(`그룹 삭제에 실패했습니다. - ${error.response?.data?.msg}`);
                },
            });
        });
    }, [groupId, deleteGroupMutation, navigate]);

    /**
     * 뒤로가기 핸들러
     */
    const handleGoBack = useCallback(() => {
        navigate('/groups');
    }, [navigate]);

    // 로딩 중
    if (isLoading) {
        return <LoadingState title='그룹 상세' />;
    }

    // 에러 발생
    if (error) {
        return <ErrorState title='그룹 상세' message='그룹 정보를 불러오는 중 오류가 발생했습니다.' onGoBack={handleGoBack} />;
    }

    // 데이터 없음
    if (!groupInfo) {
        return <EmptyState title='그룹 상세' message='그룹 정보를 찾을 수 없습니다.' onGoBack={handleGoBack} />;
    }

    return (
        <Container title='그룹 상세'>
            <Stack spacing={3}>
                {/* 상단 버튼 영역 */}
                <Box display='flex' justifyContent='space-between' alignItems='center' flexWrap='wrap' gap={2}>
                    <Button
                        variant='outlined'
                        onClick={handleGoBack}
                        startIcon={<ArrowBackIcon />}
                        sx={{ minWidth: 120 }}
                    >
                        목록으로
                    </Button>
                    <Stack direction='row' spacing={1}>
                        <Button
                            variant='outlined'
                            color='error'
                            onClick={handleDelete}
                            startIcon={<DeleteIcon />}
                            disabled={deleteGroupMutation.isPending}
                            sx={{ minWidth: 100 }}
                        >
                            삭제
                        </Button>
                        <Button
                            variant='contained'
                            onClick={handleSubmit(onSubmit)}
                            startIcon={<SaveIcon />}
                            disabled={!isDirty || updateGroupMutation.isPending}
                            sx={{ minWidth: 100 }}
                        >
                            저장
                        </Button>
                    </Stack>
                </Box>

                <Grid container spacing={3}>
                    {/* 왼쪽: 그룹 기본 정보 */}
                    <Grid item xs={12} md={8}>
                        <StyledCard>
                            <CardContent sx={{ p: 3 }}>
                                <SectionHeader>
                                    <GroupIcon sx={{ color: '#1976d2', fontSize: 28 }} />
                                    <Typography variant='h5' fontWeight={600}>
                                        그룹 정보
                                    </Typography>
                                </SectionHeader>

                                <Grid container spacing={3}>
                                    {/* 그룹 ID */}
                                    <Grid item xs={12} sm={6}>
                                        <InfoLabel>그룹 ID</InfoLabel>
                                        <Input
                                            control={control}
                                            name='groupId'
                                            textFieldProps={{
                                                fullWidth: true,
                                                disabled: true,
                                                variant: 'outlined',
                                                sx: { '& .MuiInputBase-input': { fontWeight: 500 } }
                                            }}
                                        />
                                    </Grid>

                                    {/* 고객사명 */}
                                    <Grid item xs={12} sm={6}>
                                        <InfoLabel>고객사명 *</InfoLabel>
                                        <Input
                                            control={control}
                                            name='groupName'
                                            textFieldProps={{
                                                fullWidth: true,
                                                required: true,
                                                variant: 'outlined',
                                            }}
                                            rules={{
                                                required: '고객사명을 입력해주세요.',
                                            }}
                                        />
                                        <ErrorMessage errors={errors} name='groupName' />
                                    </Grid>

                                    {/* 그룹명 */}
                                    <Grid item xs={12} sm={6}>
                                        <InfoLabel>그룹명 *</InfoLabel>
                                        <Input
                                            control={control}
                                            name='groupCourse'
                                            textFieldProps={{
                                                fullWidth: true,
                                                required: true,
                                                variant: 'outlined',
                                            }}
                                            rules={{
                                                required: '그룹명을 입력해주세요.',
                                            }}
                                        />
                                        <ErrorMessage errors={errors} name='groupCourse' />
                                    </Grid>

                                    {/* 차수 */}
                                    <Grid item xs={12} sm={6}>
                                        <InfoLabel>차수</InfoLabel>
                                        <Input
                                            control={control}
                                            name='groupCourseCount'
                                            textFieldProps={{
                                                fullWidth: true,
                                                type: 'number',
                                                variant: 'outlined',
                                            }}
                                        />
                                    </Grid>

                                    {/* 시작일 */}
                                    <Grid item xs={12} sm={6}>
                                        <InfoLabel>시작일 *</InfoLabel>
                                        <Input
                                            control={control}
                                            name='groupStartDate'
                                            textFieldProps={{
                                                fullWidth: true,
                                                type: 'date',
                                                required: true,
                                                variant: 'outlined',
                                                InputLabelProps: {
                                                    shrink: true,
                                                },
                                            }}
                                            rules={{
                                                required: '시작일을 선택해주세요.',
                                            }}
                                        />
                                        <ErrorMessage errors={errors} name='groupStartDate' />
                                    </Grid>

                                    {/* 종료일 */}
                                    <Grid item xs={12} sm={6}>
                                        <InfoLabel>종료일 *</InfoLabel>
                                        <Input
                                            control={control}
                                            name='groupDueDate'
                                            textFieldProps={{
                                                fullWidth: true,
                                                type: 'date',
                                                required: true,
                                                variant: 'outlined',
                                                InputLabelProps: {
                                                    shrink: true,
                                                },
                                            }}
                                            rules={{
                                                required: '종료일을 선택해주세요.',
                                            }}
                                        />
                                        <ErrorMessage errors={errors} name='groupDueDate' />
                                    </Grid>

                                    {/* 평가ID */}
                                    <Grid item xs={12} sm={6}>
                                        <InfoLabel>평가ID</InfoLabel>
                                        <Input
                                            control={control}
                                            name='groupAssessId'
                                            textFieldProps={{
                                                fullWidth: true,
                                                type: 'number',
                                                variant: 'outlined',
                                            }}
                                        />
                                    </Grid>

                                    {/* AI 코드 리뷰 사용 여부 */}
                                    <Grid item xs={12} sm={6}>
                                        <InfoLabel>프로젝트 개발훈련 사용가능 여부</InfoLabel>
                                        <Controller
                                            control={control}
                                            name="aiCodeReviewEnabled"
                                            render={({ field: { value, onChange } }) => (
                                                <FormControlLabel
                                                    control={
                                                        <Switch
                                                            checked={value || false}
                                                            onChange={(e) => onChange(e.target.checked)}
                                                            color="primary"
                                                        />
                                                    }
                                                    label={value ? '사용' : '미사용'}
                                                />
                                            )}
                                        />
                                    </Grid>

                                    {/* 수료 조건 */}
                                    <Grid item xs={12}>
                                        <InfoLabel>수료 조건</InfoLabel>
                                        <Controller
                                            control={control}
                                            name='completionRequirements'
                                            render={({ field: { value, onChange } }) => (
                                                <FormGroup>
                                                    <Grid container spacing={1}>
                                                        {selectableCompletionRequirements.map((option) => (
                                                            <Grid item xs={12} sm={6} key={option.value}>
                                                                <FormControlLabel
                                                                    sx={{ alignItems: 'flex-start' }}
                                                                    control={
                                                                        <Checkbox
                                                                            disabled={option.disable}
                                                                            checked={Array.isArray(value) && value.includes(option.value)}
                                                                            onChange={(e) => {
                                                                                const newList = e.target.checked
                                                                                    ? [...(Array.isArray(value) ? value : []), option.value]
                                                                                    : (Array.isArray(value) ? value.filter(type => type !== option.value) : []);
                                                                                onChange(newList);
                                                                            }}
                                                                        />
                                                                    }
                                                                    label={<CompletionRequirementOptionLabel option={option} />}
                                                                />
                                                            </Grid>
                                                        ))}
                                                    </Grid>
                                                </FormGroup>
                                            )}
                                        />
                                        {Array.isArray(selectedCompletionRequirements) && selectedCompletionRequirements.length > 0 && (
                                            <Box mt={1}>
                                                <Stack direction='row' spacing={1} flexWrap='wrap' gap={1}>
                                                    {selectedCompletionRequirements.map((contentType) => {
                                                        const option = selectableCompletionRequirements.find((opt) => opt.value === contentType);
                                                        return option ? (
                                                            <Chip
                                                                key={contentType}
                                                                label={option.label}
                                                                size='small'
                                                                color='secondary'
                                                                variant='outlined'
                                                            />
                                                        ) : null;
                                                    })}
                                                </Stack>
                                            </Box>
                                        )}
                                        <GroupRequirementAlerts issues={requirementIssues} sx={{ mt: 2 }} />
                                    </Grid>
                                </Grid>
                            </CardContent>
                        </StyledCard>
                    </Grid>

                    {/* 오른쪽: 과정 선택 */}
                    <Grid item xs={12} md={4}>
                        <StyledCard>
                            <CardContent sx={{ p: 3 }}>
                                <SectionHeader>
                                    <GroupIcon sx={{ color: '#1976d2', fontSize: 28 }} />
                                    <Typography variant='h5' fontWeight={600}>
                                        과정 선택
                                    </Typography>
                                </SectionHeader>
                                
                                <CourseSelectPanel
                                    control={control}
                                    name="courseIdList"
                                    initialCourses={courseSummary}
                                    onSelectedCoursesChange={handleSelectedCoursesChange}
                                />
                                <GroupRequirementAlerts issues={requirementIssues} sx={{ mt: 2 }} />
                            </CardContent>
                        </StyledCard>
                    </Grid>
                </Grid>
            </Stack>
        </Container>
    );
};

export default GroupDetailPage;
